<template>
  <section role="main" class="content-body">
    <header class="page-header">
      <h2>校外賃居租金補貼申請</h2>
      <div class="right-wrapper">
        <ol class="breadcrumbs">
          <li>
            <a href="/">
              <i class="fa fa-home"></i>
            </a>
          </li>
          <li><span>賃居管理</span></li>
          <li><span>校外賃居租金補貼申請</span></li>
        </ol>
      </div>
    </header>
    <div class="main" v-loading="isLoading">
      <!-- Step 0: Treaty (privacy declaration) — student new applications only -->
      <section class="panel" v-if="isShowTreaty">
        <treaty :key="$route.fullPath" type="rent-subsidy"></treaty>
      </section>

      <!-- Step 1: Application Form -->
      <section class="panel" v-if="step === 1">
        <div class="panel-body container-fluid">
          <!-- ===== Section A: 申請人資訊 ===== -->
          <div class="row form-group" style="margin-bottom: 0;">
            <div class="col-md-6">
              <h4 class="panel-title" style="font-weight: bold; margin-bottom: 15px;">申請人資訊</h4>
            </div>
            <div class="col-md-6 text-right">
              <button class="btn btn-primary" @click="showDormModal = true">歷年住宿紀錄與違規紀錄</button>
            </div>
          </div>
          <div class="row form-group">
            <div class="col-md-3 form-group">
              <label class="col-sm-4 col-xs-2">姓名</label>
              <div class="col-sm-8 col-xs-10">{{ form.name }}</div>
            </div>
            <div class="col-md-3 form-group">
              <label class="col-sm-4 col-xs-2">身分證字號</label>
              <div class="col-sm-8 col-xs-10">{{ form.idNo }}</div>
            </div>
            <div class="col-md-3 form-group">
              <label for="account" class="col-sm-4 col-xs-2">學號</label>
              <div v-if="mode === 'admin-add'" class="col-sm-8 col-xs-10">
                <input 
                  id="account" 
                  name="account" 
                  v-model="form.account" 
                  type="text" 
                  class="form-control" 
                  placeholder="請輸入學號" 
                  @blur="onAccountBlur"
                />
              </div>
              <div v-else class="col-sm-8 col-xs-10">{{ form.account }}</div>
            </div>
            <div class="col-md-3 form-group">
              <label class="col-sm-4 col-xs-2">學制</label>
              <div class="col-sm-8 col-xs-10">{{ form.ssymStr }}</div>
            </div>
          </div>
          <div class="row form-group">
            <div class="col-md-3 form-group">
              <label class="col-sm-4 col-xs-2">科系</label>
              <div class="col-sm-8 col-xs-10">{{ form.deptStr || form.dept }}</div>
            </div>
            <div class="col-md-3 form-group">
              <label class="col-sm-4 col-xs-2">年級</label>
              <div class="col-sm-8 col-xs-10">{{ form.grade }}</div>
            </div>
            <div class="col-md-3 form-group">
              <label class="col-sm-4 col-xs-2">班級</label>
              <div class="col-sm-8 col-xs-10">{{ form.class }}</div>
            </div>
            <div class="col-md-3 form-group">
              <label class="col-sm-4 col-xs-2">行動電話</label>
              <div class="col-sm-8 col-xs-10">{{ form.phone }}</div>
            </div>
          </div>
          <div class="row form-group">
            <div class="col-md-6 form-group">
              <label class="col-sm-2 col-xs-2">電子郵件</label>
              <div class="col-sm-10 col-xs-10">{{ form.email }}</div>
            </div>
            <div class="col-md-6 form-group">
              <label class="col-sm-2 col-xs-2">入款帳戶</label>
              <div class="col-sm-10 col-xs-10">{{ form.bankAccount }}</div>
            </div>
          </div>

          <hr class="hrline" />

          <!-- ===== Section B: 賃居資訊 ===== -->
          <h4 class="panel-title" style="font-weight: bold; margin-bottom: 15px;">賃居資訊</h4>
          <div class="row form-group">
            <div class="col-md-3 form-group">
              <label for="rentStartDate" class="col-sm-4 col-xs-2 column-required">租賃起日</label>
              <div class="col-sm-8 col-xs-10">
                <datepicker
                  v-if="isEditable"
                  :format="customFormatter"
                  :language="zh"
                  v-model="form.rentStartDate"
                  name="rentStartDate"
                  input-class="form-control topIndex"
                  :style="{width:'auto', display:'inline-block'}"
                />
                <span v-else>{{ form.rentStartDate ? customFormatter(form.rentStartDate) : '' }}</span>
              </div>
            </div>
            <div class="col-md-3 form-group">
              <label for="rentEndDate" class="col-sm-4 col-xs-2 column-required">租賃迄日</label>
              <div class="col-sm-8 col-xs-10">
                <datepicker
                  v-if="isEditable"
                  :format="customFormatter"
                  :language="zh"
                  v-model="form.rentEndDate"
                  name="rentEndDate"
                  input-class="form-control topIndex"
                  :style="{width:'auto', display:'inline-block'}"
                />
                <span v-else>{{ form.rentEndDate ? customFormatter(form.rentEndDate) : '' }}</span>
              </div>
            </div>
            <div class="col-md-3 form-group">
              <label for="landlordName" class="col-sm-4 col-xs-2 column-required">出租人姓名</label>
              <div class="col-sm-8 col-xs-10">
                <input
                  v-if="isEditable"
                  id="landlordName"
                  name="landlordName"
                  v-model="form.landlordName"
                  type="text"
                  class="form-control"
                  maxlength="50"
                />
                <span v-else>{{ form.landlordName }}</span>
              </div>
            </div>
            <div class="col-md-3 form-group">
              <label for="monthlyRent" class="col-sm-4 col-xs-2 column-required">每月平均租金</label>
              <div class="col-sm-8 col-xs-10">
                <input
                  v-if="isEditable"
                  id="monthlyRent"
                  name="monthlyRent"
                  v-model.number="form.monthlyRent"
                  type="number"
                  class="form-control"
                  min="0"
                />
                <span v-else>{{ form.monthlyRent != null ? form.monthlyRent + ' 元' : '' }}</span>
              </div>
            </div>
          </div>
          <div class="row form-group">
            <div class="col-md-12 form-group">
              <label for="rentAddress" class="col-sm-1 col-xs-2 column-required">租賃地址</label>
              <div class="col-sm-11 col-xs-10">
                <input
                  v-if="isEditable"
                  id="rentAddress"
                  name="rentAddress"
                  v-model="form.rentAddress"
                  type="text"
                  class="form-control"
                  maxlength="200"
                />
                <span v-else>{{ form.rentAddress }}</span>
              </div>
            </div>
          </div>

          <hr class="hrline" />

          <!-- ===== Section C: 上傳租屋契約影本 ===== -->
          <h4 class="panel-title" style="font-weight: bold; margin-bottom: 15px;">上傳租屋契約影本</h4>
          <div class="row form-group">
            <div class="col-md-9 form-group">
              <label for="contractFile" class="col-sm-4 col-xs-2 column-required">租屋契約影本 (只能上傳 pdf)</label>
              <!-- Already uploaded: show preview -->
              <div v-if="contractFilePreview" class="col-sm-8 col-xs-10">
                <div>
                  {{ getFileName(contractFilePreview) }}
                  <a v-if="isEditable" type="button" @click="resetFileUpload" style="color: #337ab7; cursor: pointer;">重傳檔案</a>
                </div>
                <a :href="getPreviewUrl(contractFilePreview)" style="color: #337ab7" target="_blank">檔案預覽</a>
              </div>
              <!-- New file selected (not yet uploaded) -->
              <div v-else-if="contractFile" class="col-sm-8 col-xs-10">
                <div>
                  {{ contractFile.name }}
                  <a type="button" @click="resetFileUpload" style="color: #337ab7; cursor: pointer;">重新選擇</a>
                </div>
              </div>
              <!-- No file: show file input -->
              <div v-else-if="isEditable" class="col-sm-8 col-xs-10">
                <input
                  type="file"
                  id="contractFile"
                  name="contractFile"
                  accept=".pdf"
                  @change="onFileChange($event)"
                />
              </div>
              <!-- Read-only, no file -->
              <div v-else class="col-sm-8 col-xs-10">
                <span>未上傳檔案</span>
              </div>
            </div>
          </div>

          <hr class="hrline" />

          <!-- ===== Section D: 檢附資料確認 ===== -->
          <h4 class="panel-title" style="font-weight: bold; margin-bottom: 15px;">檢附資料確認</h4>
          <div class="row form-group" style="margin-left: 1em;">
            <div class="col-md-12">
              1.租屋契約影本　
              <label style="font-weight: normal;"><input type="radio" :value="true" v-model="form.hasContractFile" disabled> 是</label>　
              <label style="font-weight: normal;"><input type="radio" :value="false" v-model="form.hasContractFile" disabled> 否</label>
            </div>
            <div class="col-md-12" style="margin-top: 8px;">
              2.是否有填寫賃居管理系統　
              <label style="font-weight: normal;"><input type="radio" :value="true" v-model="form.hasTenancyRegister" disabled> 是</label>　
              <label style="font-weight: normal;"><input type="radio" :value="false" v-model="form.hasTenancyRegister" disabled> 否</label>
            </div>
            <div class="col-md-12" style="margin-top: 8px;">
              3.是否有申請{{ form.schYear || '' }}學年度宿舍　
              <label style="font-weight: normal;"><input type="radio" :value="true" v-model="form.hasAppliedDorm" disabled> 是</label>　
              <label style="font-weight: normal;"><input type="radio" :value="false" v-model="form.hasAppliedDorm" disabled> 否</label>
            </div>
            <div class="col-md-12" style="margin-top: 8px;">
              4.住宿期間是否有違規紀錄？　
              <label style="font-weight: normal;"><input type="radio" :value="true" v-model="form.hasViolation" disabled> 是</label>　
              <label style="font-weight: normal;"><input type="radio" :value="false" v-model="form.hasViolation" disabled> 否</label>
            </div>
            <!-- 違規紀錄摘要表 -->
            <div class="col-md-12" style="margin-top: 10px;" v-if="violationRecords.length > 0">
              <table class="table table-bordered table-striped table-condensed" style="width: 80% !important;">
                <thead>
                  <tr>
                    <th class="text-center" style="width: 50%;">學期</th>
                    <th class="text-center" style="width: 50%;">總扣點數</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(v, idx) in violationSummary" :key="idx">
                    <td class="text-center">{{ v.smtr }}</td>
                    <td class="text-center">{{ v.totalPoints }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <hr class="hrline" />

          <!-- ===== Section E: 申請資訊說明 ===== -->
          <div class="row" style="margin-left: 1em; margin-top: 1em; margin-bottom: 1em;" v-if="infoDescription">
            <div v-html="infoDescription"></div>
            <label style="font-weight: normal; margin-top: 10px;" v-if="isEditable"><input type="checkbox" v-model="agreeCheckBox"> 同意</label>
          </div>

          <hr class="hrline" />

          <!-- ===== Section G: Buttons ===== -->
          <div class="row datatables-footer" v-if="!isAdminUser">
            <div class="col-sm-12 text-center">
              <a class="btn-upload btn s_btn_g" @click="goBack">返回</a>
              <a class="btn-upload btn s_btn_o" @click="handleSave(true)" v-if="isEditable" style="margin-left: 10px;">儲存</a>
              <a class="btn-upload btn s_btn_o" @click="handleSaveAndPreview" v-if="isEditable" style="margin-left: 10px;">儲存並預覽列印</a>
              <a class="btn-upload btn" @click="showPrintPreview = true" v-if="!isEditable && form.state !== '0'" style="margin-left: 10px; background-color:#337AB7; border-color:#2E6DA4; color:#FFF;"><i class="fa fa-print"></i> 預覽列印</a>
            </div>
          </div>
          <div class="text-center form-group" v-if="isAdminUser">
            <button class="btn" @click="goBack">返回</button>
            <button class="btn s_btn_b" @click="handleSave(true)" v-if="isEditable">送出</button>
            <button class="btn btn-default" @click="showPrintPreview = true" v-if="!isEditable && form.state !== '0'">
              <i class="fa fa-print"></i> 預覽列印
            </button>
          </div>

          <!-- ===== Section F: Admin Review Form (admin-only) ===== -->
          <template v-if="isReviewMode && form.state !== '0'">
            <hr class="hrline" />
            <h4 class="panel-title" style="font-weight: bold; margin-bottom: 15px;">審核</h4>
            <div class="row form-group">
              <div class="col-md-4 form-group">
                <label for="reviewState" class="col-sm-4 col-xs-2 column-required">審核狀態</label>
                <div class="col-sm-8 col-xs-10">
                  <select id="reviewState" name="reviewState" v-model="reviewForm.state" class="form-control" @change="handleReviewStateChange">
                    <option value="1">待審</option>
                    <option value="2">審核通過</option>
                    <option value="3">審核不通過</option>
                    <option value="4">退回補件</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="row form-group" v-if="reviewForm.state === '3'">
              <div class="col-md-12 form-group">
                <label class="col-sm-1 col-xs-2 column-required">不通過原因</label>
                <div class="col-sm-11 col-xs-10">
                  <label v-for="option in rejectReasonOptions" :key="option.value" style="font-weight: normal; margin-right: 20px;">
                    <input type="checkbox" :value="option.value" v-model="reviewFailReasons"> {{ option.label }}
                  </label>
                </div>
              </div>
            </div>
            <div class="row form-group" v-if="showReviewRemark">
              <div class="col-md-12 form-group">
                <label for="reviewRemark" class="col-sm-1 col-xs-2" :class="{ 'column-required': isReviewRemarkRequired }">說明</label>
                <div class="col-sm-11 col-xs-10">
                  <textarea
                    id="reviewRemark"
                    name="reviewRemark"
                    v-model="reviewForm.remark"
                    rows="3"
                    class="form-control"
                    :placeholder="reviewForm.state === '3' ? '可填寫其他說明' : (isReviewRemarkRequired ? '請填寫原因' : '請填寫說明')"
                  ></textarea>
                </div>
              </div>
            </div>
            <div class="text-center form-group">
              <button class="btn s_btn_b" @click="handleReview">送出審核</button>
            </div>

            <!-- 審核紀錄（管理員也看到） -->
            <div class="row form-group" v-if="reviewLogs.length > 0" style="margin-top: 20px;">
              <div class="col-md-12">
                <h4 class="panel-title" style="font-weight: bold; margin-bottom: 10px;">審核紀錄</h4>
                <table class="table table-bordered table-striped table-condensed">
                  <thead>
                    <tr>
                      <th>審核時間</th>
                      <th>審核人員</th>
                      <th>審核結果</th>
                      <th>備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(log, idx) in reviewLogs" :key="idx">
                      <td>{{ customFormatter(log.reviewDate, 'yyyy/MM/DD HH:mm:ss') }}</td>
                      <td>{{ log.reviewer }}</td>
                      <td>{{ log.reviewStateStr }}</td>
                      <td>{{ log.remark }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>

          <!-- ===== Review Status & History (students only) ===== -->
          <template v-if="!isAdminUser && form.state && form.state !== '0'">
            <hr class="hrline" />
            <div class="row form-group">
              <div class="col-md-12">
                <h4 class="panel-title" style="font-weight: bold; margin-bottom: 10px;">審核狀態</h4>
                <p style="font-size: 16px;">
                  目前狀態：<span :class="{'text-warning': form.state === '1', 'text-success': form.state === '2', 'text-danger': form.state === '3', 'text-info': form.state === '4'}">
                    <strong>{{ stateStr }}</strong>
                  </span>
                </p>
              </div>
            </div>
            <div class="row form-group" v-if="reviewLogs.length > 0">
              <div class="col-md-12">
                <h4 class="panel-title" style="font-weight: bold; margin-bottom: 10px;">審核紀錄</h4>
                <table class="table table-bordered table-striped table-condensed">
                  <thead>
                    <tr>
                      <th>審核時間</th>
                      <th>審核人員</th>
                      <th>審核結果</th>
                      <th>備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(log, idx) in reviewLogs" :key="idx">
                      <td>{{ log.reviewDate }}</td>
                      <td>{{ log.reviewer }}</td>
                      <td>{{ log.reviewStateStr }}</td>
                      <td>{{ log.remark }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>

        </div>
      </section>

      <!-- 歷年住宿紀錄與違規紀錄 Modal -->
      <div class="modal" :class="{ 'fade in': showDormModal }" :style="{ display: showDormModal ? 'block' : 'none' }" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-lg" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <button type="button" class="close" @click="showDormModal = false">
                <span aria-hidden="true">&times;</span>
              </button>
              <h4 class="modal-title">歷年住宿紀錄與違規紀錄</h4>
            </div>
            <div class="modal-body">
              <!-- 宿舍類別篩選 -->
              <div class="row form-group">
                <div class="col-md-4">
                  <label class="col-sm-4">宿舍類別</label>
                  <div class="col-sm-8">
                    <select v-model="dormTypeFilter" class="form-control">
                      <option value="一般宿舍">一般宿舍</option>
                      <option value="寒暑假宿舍">寒暑假宿舍</option>
                    </select>
                  </div>
                </div>
              </div>

              <h5 style="font-weight: bold;">住宿紀錄</h5>
              <table class="table table-bordered table-striped table-condensed" v-if="filteredDormRecords.length > 0">
                <thead>
                  <tr>
                    <th class="text-center">學年度</th>
                    <th class="text-center">學期</th>
                    <th class="text-center">宿舍名稱</th>
                    <th class="text-center">房號</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(rec, idx) in filteredDormRecords" :key="idx">
                    <td class="text-center">{{ rec.schYear }}</td>
                    <td class="text-center">{{ rec.smtr }}</td>
                    <td class="text-center">{{ rec.dormName }}</td>
                    <td class="text-center">{{ rec.roomNo }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else>無住宿紀錄</p>

              <h5 style="font-weight: bold; margin-top: 20px;">違規紀錄</h5>
              <table class="table table-bordered table-striped table-condensed" v-if="filteredViolationRecords.length > 0">
                <thead>
                  <tr>
                    <th class="text-center">學年度</th>
                    <th class="text-center">學期</th>
                    <th class="text-center">違規日期</th>
                    <th class="text-center">違規事項</th>
                    <th class="text-center">扣點</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(v, idx) in filteredViolationRecords" :key="idx">
                    <td class="text-center">{{ v.schYear }}</td>
                    <td class="text-center">{{ v.smtr }}</td>
                    <td class="text-center">{{ v.violationDate ? v.violationDate.substring(0,10) : '' }}</td>
                    <td>{{ v.violationItem }}</td>
                    <td class="text-center">{{ v.penaltyPoints }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else>無違規紀錄</p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-default" @click="showDormModal = false">關閉</button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade in" v-if="showDormModal" @click="showDormModal = false"></div>

      <!-- 預覽列印 Modal -->
      <div class="modal" :class="{ 'fade in': showPrintPreview }" :style="{ display: showPrintPreview ? 'block' : 'none' }" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document" style="width: 80%;">
          <div class="modal-content">
            <div class="modal-body">
              <button type="button" class="bootbox-close-button close" style="margin-top: -10px;" @click="showPrintPreview = false">&times;</button>
              <div class="bootbox-body">
                <div id="printArea" class="body-by-A4">
                  <h3 align="center">亞東學校財團法人亞東科技大學校外住宿租金補貼申請書</h3>
                  <span>{{ form.schYear }}學年度&emsp;第{{ form.smtr }}學期</span>
                  <span style="float: right;">申請日期：{{ nowYear }}&emsp;年&emsp;{{ nowMonth }}&emsp;月&emsp;{{ nowDay }}&emsp;日</span>
                  <table class="table table-no-more table-bordered table-striped mb-none">
                    <tbody>
                      <tr>
                        <td class="text-center" colspan="4">申請人資訊</td>
                      </tr>
                      <tr>
                        <td class="text-center" width="3%">學制</td>
                        <td class="text-left" colspan="3">
                          <input type="checkbox" :checked="ssymIsDay" disabled>日間部&emsp;<input type="checkbox" :checked="!ssymIsDay" disabled>進修部
                        </td>
                      </tr>
                      <tr>
                        <td class="text-center" width="3%">學號</td>
                        <td class="text-left" width="3%">{{ form.account }}</td>
                        <td class="text-center" width="3%">系所及年級</td>
                        <td class="text-left" width="3%">{{ form.deptStr || form.dept }} 系 {{ form.grade }} 年級 {{ form.class }} 班</td>
                      </tr>
                      <tr>
                        <td class="text-center" width="3%">姓名</td>
                        <td class="text-left" width="3%">{{ form.name }}</td>
                        <td class="text-center" width="3%">連絡電話</td>
                        <td class="text-left" width="3%">{{ form.phone }}</td>
                      </tr>
                      <tr>
                        <td class="text-center">電子郵件</td>
                        <td class="text-left" colspan="3">{{ form.email }}</td>
                      </tr>
                      <tr>
                        <td class="text-center" colspan="4">租賃資訊</td>
                      </tr>
                      <tr>
                        <td class="text-center" width="3%">租賃起期<br>租賃迄期</td>
                        <td class="text-left" width="3%">自 {{ formatPrintDate(form.rentStartDate) }}<br>至 {{ formatPrintDate(form.rentEndDate) }}</td>
                        <td class="text-center" width="3%">出租人姓名</td>
                        <td class="text-left" width="3%">{{ form.landlordName }}</td>
                      </tr>
                      <tr>
                        <td class="text-center" width="3%">每月平均租金</td>
                        <td class="text-left" width="3%">{{ form.monthlyRent }}</td>
                        <td class="text-center" width="3%">入款帳戶</td>
                        <td class="text-left" width="3%">{{ form.bankAccount }}</td>
                      </tr>
                      <tr>
                        <td class="text-center" width="3%">租賃地址</td>
                        <td class="text-left" colspan="3">{{ form.rentAddress }}</td>
                      </tr>
                      <tr>
                        <td class="text-center" width="3%">檢附資料確認</td>
                        <td class="text-left" colspan="3">
                          <input type="checkbox" :checked="form.hasContractFile" disabled>租屋契約影本<br>
                          <input type="checkbox" checked disabled>是否有填寫賃居管理系統&emsp;<input type="checkbox" :checked="form.hasTenancyRegister" disabled>是<input type="checkbox" :checked="!form.hasTenancyRegister" disabled>否<br>
                          <input type="checkbox" checked disabled>是否有申請{{ form.schYear }}學年度宿舍&emsp;<input type="checkbox" :checked="form.hasAppliedDorm" disabled>是<input type="checkbox" :checked="!form.hasAppliedDorm" disabled>否<br>
                          <input type="checkbox" checked disabled>住宿期間是否有違規紀錄？&emsp;<input type="checkbox" :checked="form.hasViolation" disabled>是<input type="checkbox" :checked="!form.hasViolation" disabled>否，勾是請填寫違規點數共{{ totalPenaltyPoints || '____' }}點<br>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <table class="table table-no-more table-bordered table-striped mb-none">
                    <tbody>
                      <tr>
                        <td class="text-center" width="10%">申請資訊說明</td>
                        <td class="text-left" colspan="3">
                          <div v-html="infoDescription"></div>
                        </td>
                      </tr>
                      <tr>
                        <td class="text-left" colspan="4">
                          <span style="font-weight: 500;">注意事項：</span><br>
                          1.申請人請完整填寫本表。<br>
                          2.申請人請確實將租賃契約影本黏貼於本表或檢附於本表格後，若有偽造、變造事實者，經查證屬實者，依相關規定懲處及追討補助金額；涉及刑事責任者，移送司法機關辦理。<br>
                          3.申請人請詳閱隱私權及個人資料保護政策宣告書並簽名，以保障您的權利。
                          <br><br>
                          <span class="s_right">申請人簽章：___________________________</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <table class="table table-no-more table-bordered table-striped mb-none">
                    <tbody>
                      <tr>
                        <td class="text-center" width="20%">學生事務處生活輔導組填寫欄位</td>
                        <td class="text-left">
                          審核結果：<input type="checkbox" :checked="reviewForm.state === '2'" disabled>通過&emsp;<input type="checkbox" :checked="reviewForm.state === '3'" disabled>未通過<br>
                          不通過原因：<input type="checkbox" :checked="hasRejectReason('未申請學生宿舍')" disabled>未申請學生宿舍&emsp;<input type="checkbox" :checked="hasRejectReason('住宿期間有違規紀錄未完成銷點')" disabled>住宿期間有違規紀錄未完成銷點
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <table class="table table-no-more table-bordered table-striped mb-none">
                    <tbody>
                      <tr>
                        <td class="text-center" width="3%">生活輔導組&emsp;承辦人</td>
                        <td class="text-center" width="3%">生活輔導組&emsp;組長</td>
                        <td class="text-center" width="3%">學務長</td>
                      </tr>
                      <tr>
                        <td class="text-left" width="3%" style="padding: 2.5em 0;"></td>
                        <td class="text-left" width="3%" style="padding: 2.5em 0;"></td>
                        <td class="text-left" width="3%" style="padding: 2.5em 0;"></td>
                      </tr>
                      <tr>
                        <td class="text-center" width="3%">會計室</td>
                        <td class="text-center" width="3%">主任秘書</td>
                        <td class="text-center" width="3%">校長</td>
                      </tr>
                      <tr>
                        <td class="text-left" width="3%" style="padding: 2.5em 0;"></td>
                        <td class="text-left" width="3%" style="padding: 2.5em 0;"></td>
                        <td class="text-left" width="3%" style="padding: 2.5em 0;"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <table width="100%"><br>
                  <tr>
                    <td align="center"><a href="javascript:;" @click="handlePrint" class="btn-upload btn" style="background-color:#337AB7; border-color:#2E6DA4; color:#FFF;">列　印</a></td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade in" v-if="showPrintPreview" @click="showPrintPreview = false"></div>
    </div>
  </section>
</template>

<script>
import treaty from './treaty';

export default {
  components: { treaty },
  data() {
    return {
      mode: '',
      step: 0,
      isLoading: true,
      applyId: null,
      form: {
        schYear: '',
        smtr: '',
        account: '',
        name: '',
        idNo: '',
        ssym: '',
        ssymStr: '',
        dept: '',
        deptStr: '',
        grade: '',
        class: '',
        phone: '',
        email: '',
        bankAccount: '',
        rentStartDate: null,
        rentEndDate: null,
        monthlyRent: null,
        landlordName: '',
        rentAddress: '',
        hasTenancyRegister: null,
        hasAppliedDorm: null,
        hasViolation: null,
        hasContractFile: null,
        contractFilePath: '',
        state: '0',
        treatyVersion: ''
      },
      contractFile: null,
      contractFilePreview: null,
      dormRecords: [],
      violationRecords: [],
      totalPenaltyPoints: 0,
      reviewLogs: [],
      reviewForm: { state: '1', remark: '' },
      reviewFailReasons: [],
      rejectReasonOptions: [
        { value: '未申請學生宿舍', label: '未申請學生宿舍' },
        { value: '住宿期間有違規紀錄未完成銷點', label: '住宿期間有違規紀錄未完成銷點' }
      ],
      stateStr: '',
      showDormModal: false,
      dormTypeFilter: '一般宿舍',
      showPrintPreview: false,
      agreeCheckBox: false,
      infoDescription: ''
    };
  },
  computed: {
    isAdminUser() {
      return this.isAdmin('tenancy');
    },
    violationSummary() {
      if (!this.violationRecords || this.violationRecords.length === 0) return [];
      const summary = {};
      this.violationRecords.forEach(v => {
        const key = `${v.schYear}-${v.smtr}`;
        if (!summary[key]) {
          summary[key] = { smtr: `${v.schYear}-${v.smtr}`, totalPoints: 0 };
        }
        summary[key].totalPoints += v.penaltyPoints || 0;
      });
      return Object.values(summary);
    },
    isEditable() {
      if (!this.isAdminUser) {
        return this.form.state === '0' || this.form.state === '4';
      }
      if (this.mode === 'admin-add') return true;
      return false;
    },
    isReviewMode() {
      return this.mode === 'review' && this.isAdminUser;
    },
    showReviewRemark() {
      return ['2', '3', '4'].includes(this.reviewForm.state);
    },
    isReviewRemarkRequired() {
      return this.reviewForm.state === '4';
    },
    isShowTreaty() {
      return this.step === 0 && this.mode === 'student-apply';
    },
    ssymIsDay() {
      const ssym = this.form.ssym || this.form.ssymStr || '';
      return ssym.indexOf('進修') === -1;
    },
    totalPenaltyPoints() {
      if (!this.violationRecords || this.violationRecords.length === 0) return 0;
      return this.violationRecords.reduce((sum, v) => sum + (v.penaltyPoints || 0), 0);
    },
    nowYear() {
      return new Date().getFullYear();
    },
    nowMonth() {
      return new Date().getMonth() + 1;
    },
    nowDay() {
      return new Date().getDate();
    },
    filteredDormRecords() {
      if (!this.dormRecords || this.dormRecords.length === 0) return [];
      return this.dormRecords.filter(rec => rec.dormType === this.dormTypeFilter);
    },
    filteredViolationRecords() {
      if (!this.violationRecords || this.violationRecords.length === 0) return [];
      return this.violationRecords.filter(v => v.dormType === this.dormTypeFilter);
    }
  },
  mounted() {
    this.mode = this.$route.query.mode || 'student-apply';
    this.applyId = this.$route.query.id;
    this.loadInfoDescription();
    this.pageInit();
  },
  methods: {
    async loadInfoDescription() {
      try {
        const resp = await this.$axios.get('/api/common/treaty?type=rent-subsidy');
        if (resp.data && resp.data.InfoDescription) {
          this.infoDescription = this.htmlDecode(resp.data.InfoDescription.content || '');
        }
      } catch (err) {
        console.error('Failed to load info description:', err);
      }
    },
    async pageInit() {
      if (this.mode === 'student-apply') {
        const openResp = await this.$axios.get('/api/rentsubsidyapply/check-open');
        if (!openResp.data.data.isOpen) {
          bootbox.alert('目前未開放申請，請依公告時間提出申請');
          this.$router.go(-1);
          return;
        }
        this.form.schYear = openResp.data.data.schYear;
        this.form.smtr = openResp.data.data.smtr;

        const regResp = await this.$axios.get(`/api/rentsubsidyapply/check-register?account=${this.sessionData.account}`);
        if (!regResp.data.data.hasRegister) {
          bootbox.alert('尚未完成賃居資料填寫，無法提出申請');
          this.$router.push('/tenancy-register');
          return;
        }
        this.form.hasTenancyRegister = true;

        if (this.applyId) {
          this.step = 1;
          await this.loadApplyDetail();
        } else {
          const listResp = await this.$axios.get(
            `/api/rentsubsidyapply?account=${this.sessionData.account}&schYear=${this.form.schYear}&smtr=${this.form.smtr}&page=1&pageSize=1`
          );
          if (listResp.data.status === 'success' && listResp.data.data.rows.length > 0) {
            this.applyId = listResp.data.data.rows[0].id;
            this.step = 1;
            await this.loadApplyDetail();
          } else {
            await this.loadStudentInfo(this.sessionData.account);
            this.step = 0;
          }
        }

        await this.loadDormRecords(this.sessionData.account);
      } else if (this.mode === 'admin-add') {
        this.step = 1;
        const openResp = await this.$axios.get('/api/rentsubsidyapply/check-open');
        if (openResp.data.data.schYear) {
          this.form.schYear = openResp.data.data.schYear;
          this.form.smtr = openResp.data.data.smtr;
        }
      } else if (this.mode === 'review' && this.applyId) {
        this.step = 1;
        await this.loadApplyDetail();
        await this.loadDormRecords(this.form.account);
      }
      this.isLoading = false;
    },
    async loadStudentInfo(account) {
      const resp = await this.$axios.get(`/api/rentsubsidyapply/student-info?account=${account}`);
      if (resp.data.status === 'success') {
        const stu = resp.data.data;
        Object.assign(this.form, {
          account: stu.account,
          name: stu.name,
          idNo: stu.idno,
          ssym: stu.ssym,
          ssymStr: stu.ssymStr,
          dept: stu.dept,
          deptStr: stu.deptStr,
          grade: stu.grade,
          class: stu.class,
          phone: stu.phone,
          email: stu.email,
          bankAccount: stu.bankAccount
        });
      }
    },
    async loadApplyDetail() {
      const resp = await this.$axios.get(`/api/rentsubsidyapply/${this.applyId}`);
      if (resp.data.status === 'success') {
        const data = resp.data.data;
        Object.assign(this.form, data.apply);
        if (data.ssymStr) this.form.ssymStr = data.ssymStr;
        if (data.deptStr) this.form.deptStr = data.deptStr;
        this.stateStr = data.apply.state ? this.getStateStr(data.apply.state) : '';
        this.reviewForm.state = data.apply.state;
        this.reviewLogs = data.reviewLogs || [];
        const latestRemark = this.reviewLogs.length > 0
          ? (this.reviewLogs[0].remark || '')
          : (data.apply.rejectReason || '');
        this.applyReviewRemark(latestRemark);
        if (this.form.contractFilePath) {
          this.contractFilePreview = this.form.contractFilePath;
          this.form.hasContractFile = true;
        } else {
          this.form.hasContractFile = false;
        }
      }
    },
    async loadDormRecords(account) {
      if (!account) return;
      const resp = await this.$axios.get(`/api/rentsubsidyapply/dorm-records?account=${account}`);
      if (resp.data.status === 'success') {
        this.dormRecords = resp.data.data.dormRecords;
        this.violationRecords = resp.data.data.violationRecords;
        this.form.hasAppliedDorm = resp.data.data.hasAppliedDorm;
        this.form.hasViolation = resp.data.data.hasViolation;
        this.totalPenaltyPoints = resp.data.data.totalPenaltyPoints;
      }
    },
    getStateStr(state) {
      const map = { '0': '草稿', '1': '待審', '2': '審核通過', '3': '審核不通過', '4': '退回補件' };
      return map[state] || '';
    },
    handleReviewStateChange() {
      this.reviewForm.remark = '';
      this.reviewFailReasons = [];
    },
    parseReviewRemark(remark) {
      const lines = (remark || '')
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => line);
      const selectedReasons = [];
      const otherRemark = [];

      lines.forEach(line => {
        if (this.rejectReasonOptions.some(option => option.value === line)) {
          selectedReasons.push(line);
          return;
        }
        if (line.indexOf('其他說明：') === 0) {
          const extraRemark = line.replace('其他說明：', '').trim();
          if (extraRemark) {
            otherRemark.push(extraRemark);
          }
          return;
        }
        otherRemark.push(line);
      });

      return {
        selectedReasons,
        remark: otherRemark.join('\n')
      };
    },
    applyReviewRemark(remark) {
      const parsed = this.parseReviewRemark(remark);
      this.reviewFailReasons = parsed.selectedReasons;
      this.reviewForm.remark = parsed.remark;
    },
    buildReviewRemark() {
      if (this.reviewForm.state !== '3') {
        return (this.reviewForm.remark || '').trim();
      }

      const combinedRemark = [...this.reviewFailReasons];
      const extraRemark = (this.reviewForm.remark || '').trim();
      if (extraRemark) {
        combinedRemark.push(`其他說明：${extraRemark}`);
      }
      return combinedRemark.join('\n');
    },
    hasRejectReason(reason) {
      return this.reviewFailReasons.includes(reason);
    },
    formatPrintDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日`;
    },
    onFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (ext !== '.pdf') {
          bootbox.alert('僅允許上傳 PDF 格式的檔案');
          e.target.value = '';
          return;
        }
        this.contractFile = file;
        this.contractFilePreview = null;
        this.form.hasContractFile = true;
      }
    },
    resetFileUpload() {
      this.contractFilePreview = null;
      this.contractFile = null;
      this.form.hasContractFile = false;
    },
    async handleSave(isSubmit = false) {
      if (isSubmit) {
        const missing = [];
        if (!this.form.rentStartDate) missing.push('租賃起日');
        if (!this.form.rentEndDate) missing.push('租賃迄日');
        if (!this.form.landlordName) missing.push('出租人姓名');
        if (!this.form.monthlyRent) missing.push('每月平均租金');
        if (!this.form.rentAddress) missing.push('租賃地址');
        if (!this.form.contractFilePath && !this.contractFile) missing.push('租屋契約影本');
        if (missing.length > 0) {
          bootbox.alert(`請填寫：${missing.join('、')}`);
          return;
        }
        // 驗證租賃日期合理性
        if (this.form.rentStartDate && this.form.rentEndDate) {
          const start = new Date(this.form.rentStartDate);
          const end = new Date(this.form.rentEndDate);
          if (end <= start) {
            bootbox.alert('租賃迄日必須晚於租賃起日');
            return;
          }
          const diffDays = (end - start) / (1000 * 60 * 60 * 24);
          if (diffDays > 365) {
            bootbox.alert('租賃期間不可超過一年');
            return;
          }
        }
        if (!this.isAdminUser && !this.agreeCheckBox) {
          bootbox.alert('請勾選同意後再送出');
          return;
        }
      }

      const formData = new FormData();
      formData.append('applyData', JSON.stringify(this.form));
      formData.append('isSubmit', isSubmit);
      if (this.contractFile) {
        formData.append('contractFile', this.contractFile);
      }

      const resp = await this.$axios.post('/api/rentsubsidyapply', formData);
      if (resp.data.status === 'success') {
        this.applyId = resp.data.data.id;
        bootbox.alert('申請已送出');
        this.$router.push('/rent-subsidy/list');
      } else {
        bootbox.alert(resp.data.msg);
      }
    },
    handleSaveAndPreview() {
      this.handleSave(true).then(() => {
        if (this.applyId) {
          this.showPrintPreview = true;
        }
      });
    },
    async handleReview() {
      if (!this.reviewForm.state) {
        bootbox.alert('請選擇審核狀態');
        return;
      }
      const reviewRemark = this.buildReviewRemark();
      if (this.reviewForm.state === '3' && !reviewRemark) {
        bootbox.alert('請至少勾選一項不通過原因或填寫其他說明');
        return;
      }
      if (this.reviewForm.state === '4' && !reviewRemark) {
        bootbox.alert('請填寫原因');
        return;
      }
      const resp = await this.$axios.post('/api/rentsubsidyapply/review', {
        id: this.applyId,
        state: this.reviewForm.state,
        remark: reviewRemark
      });
      if (resp.data.status === 'success') {
        bootbox.alert('審核完成');
        await this.loadApplyDetail();
      } else {
        bootbox.alert(resp.data.msg);
      }
    },
    handlePrint() {
      const printContent = document.getElementById('printArea');
      if (printContent) {
        const printWindow = window.open('', '', 'height=600,width=800');
        printWindow.document.write('<html><head><title>校外賃居租金補貼申請表</title>');
        printWindow.document.write('<style>');
        printWindow.document.write('body { font-family: Arial, sans-serif; padding: 20px; }');
        printWindow.document.write('table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }');
        printWindow.document.write('table, th, td { border: 1px solid black; }');
        printWindow.document.write('td { padding: 8px; }');
        printWindow.document.write('.text-center { text-align: center; }');
        printWindow.document.write('.text-left { text-align: left; }');
        printWindow.document.write('h3 { text-align: center; }');
        printWindow.document.write('.s_right { float: right; }');
        printWindow.document.write('@media print { button, .btn { display: none !important; } }');
        printWindow.document.write('</style>');
        printWindow.document.write('</head><body>');
        printWindow.document.write(printContent.innerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.print();
      }
    },
    goBack() {
      this.$router.push('/rent-subsidy/list');
    },
    async adminSearchStudent() {
      if (!this.form.account || !this.form.account.trim()) {
        return;
      }
      await this.loadStudentInfo(this.form.account);
      await this.loadDormRecords(this.form.account);
      const regResp = await this.$axios.get(`/api/rentsubsidyapply/check-register?account=${this.form.account}`);
      this.form.hasTenancyRegister = regResp.data.data.hasRegister;
      
      // 新增模式：如果學生沒有填寫賃居登記表，導回清單
      if (this.mode === 'admin-add' && !regResp.data.data.hasRegister) {
        bootbox.alert('此學生尚未完成賃居資料填寫，無法建立申請', () => {
          this.$router.push('/rent-subsidy/list');
        });
      }
    },
    async onAccountBlur() {
      if (!this.form.account || !this.form.account.trim()) {
        return;
      }
      await this.adminSearchStudent();
    },
    isPdf(filePath) {
      return filePath && filePath.toLowerCase().endsWith('.pdf');
    },
    isImage(filePath) {
      if (!filePath) return false;
      const ext = filePath.toLowerCase();
      return ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.png');
    },
    getPreviewUrl(filePath) {
      return `/upload/${filePath}`;
    },
    getFileName(filePath) {
      if (!filePath) return '';
      return filePath.split('/').pop();
    }
  }
};
</script>

<style scoped>
.hrline {
  border-top: 1px solid #ddd;
  margin: 20px 0;
}
.topIndex {
  z-index: 100;
}
</style>

<style>
/* Modal styles */
.modal {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1050;
  overflow: auto;
}
.modal.fade .modal-dialog {
  transition: transform 0.3s ease-out;
  transform: translate(0, -25%);
}
.modal.fade.in .modal-dialog {
  transform: translate(0, 0);
}
.modal-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1040;
  background-color: #000;
}
.modal-backdrop.fade {
  opacity: 0;
}
.modal-backdrop.in {
  opacity: 0.5;
}
</style>
